<template>
  <div ref="root" class="rtable" :class="rootClasses" :style="cssVars">
    <div v-if="loading" class="table-overlay">
      <div class="table-spinner" />
    </div>

    <div v-if="!effectiveHideHeader && headerLines.length" class="rtable__head">
      <div
        v-for="(line, li) in headerLines"
        :key="`h-${li}`"
        class="rtable__line rtable__line--head"
        :style="lineStyle(line)"
      >
        <div
          v-for="(cell, ci) in line"
          :key="ci"
          class="rtable__cell rtable__cell--head"
          :class="[cellClasses(cell), { 'rtable__cell--sort': isSortable(cell), 'rtable__cell--empty': !cell.keys.length }]"
          @click="isSortable(cell) && $emit('sort', cell.keys[0])"
        >
          {{ headerLabel(cell) }}
          <span v-if="isSortable(cell)" class="sort-arrows">
            <span :class="['sort-arrow sort-arrow--up', { active: sort === cell.keys[0] && direction === 'asc' }]" />
            <span :class="['sort-arrow sort-arrow--down', { active: sort === cell.keys[0] && direction === 'desc' }]" />
          </span>
        </div>
      </div>
    </div>

    <!-- When the header (and its click-to-sort cells) is hidden — e.g. an accordion/card
         breakpoint — sortable columns still need a way to be triggered -->
    <div v-if="effectiveHideHeader && sortableColumns.length" class="rtable__mobile-sort">
      <button
        v-for="col in sortableColumns"
        :key="col.key"
        type="button"
        class="rtable__mobile-sort-btn"
        :class="{ 'rtable__mobile-sort-btn--active': sort === col.key }"
        @click="$emit('sort', col.key)"
      >
        {{ col.label }}
        <span class="sort-arrows">
          <span :class="['sort-arrow sort-arrow--up', { active: sort === col.key && direction === 'asc' }]" />
          <span :class="['sort-arrow sort-arrow--down', { active: sort === col.key && direction === 'desc' }]" />
        </span>
      </button>
    </div>

    <div v-if="!loading && !displayRows.length" class="rtable__empty">
      {{ emptyText || 'No results found.' }}
    </div>

    <!-- Draggable rows (no expand support) -->
    <draggable
      v-if="draggable"
      tag="div"
      class="rtable__draggable-body"
      :list="displayRows"
      handle=".rtable__drag-handle"
      @start="$emit('drag-start', displayRows.map(r => r.id))"
      @end="$emit('drag-end', displayRows.map(r => r.id))"
    >
      <div
        v-for="(row, index) in displayRows"
        :key="String(row.id)"
        class="rtable__record"
        :class="rowClasses(row, index)"
      >
        <div
          v-if="accordionSummary"
          class="rtable__line rtable__line--summary"
          :style="summaryLineStyle(accordionSummary)"
          @click="toggleRow(row.id)"
        >
          <div
            v-for="(cell, ci) in accordionSummary"
            :key="ci"
            class="rtable__cell"
            :class="[cellClasses(cell), { 'rtable__cell--merged': cell.merged, 'rtable__cell--empty': !cell.keys.length }]"
          >
            <template v-if="cell.merged">
              <div v-for="k in cell.keys" :key="k" class="rtable__sub">
                <strong v-if="!cell.hideLabel" class="rtable__sub-label">{{ labelFor(k) }}: </strong>
                <slot :name="`cell:${k}`" :row="row">{{ row[k] }}</slot>
              </div>
            </template>
            <template v-else-if="cell.keys.length">
              <strong v-if="showCellLabel(cell)" class="rtable__cell-label">{{ labelFor(cell.keys[0]) }}: </strong>
              <slot :name="`cell:${cell.keys[0]}`" :row="row">{{ row[cell.keys[0]] }}</slot>
            </template>
          </div>
          <span class="rtable__accordion-toggle" :class="{ 'rtable__accordion-toggle--open': resolvedOpenId === row.id }" />
        </div>

        <template v-if="accordionSummary">
          <div v-if="resolvedOpenId === row.id" class="rtable__accordion-detail">
            <div v-for="(cell, ci) in accordionDetailCells" :key="ci" class="rtable__accordion-field">
              <template v-if="cell.merged">
                <div v-for="k in cell.keys" :key="k">
                  <strong class="rtable__cell-label">{{ labelFor(k) }}: </strong>
                  <slot :name="`cell:${k}`" :row="row">{{ row[k] }}</slot>
                </div>
              </template>
              <template v-else-if="cell.keys.length">
                <strong class="rtable__cell-label">{{ labelFor(cell.keys[0]) }}: </strong>
                <slot :name="`cell:${cell.keys[0]}`" :row="row">{{ row[cell.keys[0]] }}</slot>
              </template>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="(line, li) in bodyLines"
            :key="li"
            class="rtable__line rtable__line--data"
            :style="lineStyle(line)"
          >
            <div
              v-for="(cell, ci) in line"
              :key="ci"
              class="rtable__cell"
              :class="[cellClasses(cell), { 'rtable__cell--merged': cell.merged, 'rtable__cell--empty': !cell.keys.length }]"
            >
              <template v-if="cell.merged">
                <div v-for="k in cell.keys" :key="k" class="rtable__sub">
                  <strong v-if="!cell.hideLabel" class="rtable__sub-label">{{ labelFor(k) }}: </strong>
                  <slot :name="`cell:${k}`" :row="row">{{ row[k] }}</slot>
                </div>
              </template>
              <template v-else-if="cell.keys.length">
                <strong v-if="showCellLabel(cell)" class="rtable__cell-label">{{ labelFor(cell.keys[0]) }}: </strong>
                <slot :name="`cell:${cell.keys[0]}`" :row="row">{{ row[cell.keys[0]] }}</slot>
              </template>
            </div>
          </div>
        </template>
      </div>
    </draggable>

    <!-- Normal rows with expand support -->
    <template v-else v-for="(row, index) in displayRows">
      <div
        :key="String(row.id)"
        class="rtable__record"
        :class="rowClasses(row, index)"
      >
        <div
          v-if="accordionSummary"
          class="rtable__line rtable__line--summary"
          :style="summaryLineStyle(accordionSummary)"
          @click="toggleRow(row.id)"
        >
          <div
            v-for="(cell, ci) in accordionSummary"
            :key="ci"
            class="rtable__cell"
            :class="[cellClasses(cell), { 'rtable__cell--merged': cell.merged, 'rtable__cell--empty': !cell.keys.length }]"
          >
            <template v-if="cell.merged">
              <div v-for="k in cell.keys" :key="k" class="rtable__sub">
                <strong v-if="!cell.hideLabel" class="rtable__sub-label">{{ labelFor(k) }}: </strong>
                <slot :name="`cell:${k}`" :row="row">{{ row[k] }}</slot>
              </div>
            </template>
            <template v-else-if="cell.keys.length">
              <strong v-if="showCellLabel(cell)" class="rtable__cell-label">{{ labelFor(cell.keys[0]) }}: </strong>
              <slot :name="`cell:${cell.keys[0]}`" :row="row">{{ row[cell.keys[0]] }}</slot>
            </template>
          </div>
          <span class="rtable__accordion-toggle" :class="{ 'rtable__accordion-toggle--open': resolvedOpenId === row.id }" />
        </div>

        <template v-if="accordionSummary">
          <div v-if="resolvedOpenId === row.id" class="rtable__accordion-detail">
            <div v-for="(cell, ci) in accordionDetailCells" :key="ci" class="rtable__accordion-field">
              <template v-if="cell.merged">
                <div v-for="k in cell.keys" :key="k">
                  <strong class="rtable__cell-label">{{ labelFor(k) }}: </strong>
                  <slot :name="`cell:${k}`" :row="row">{{ row[k] }}</slot>
                </div>
              </template>
              <template v-else-if="cell.keys.length">
                <strong class="rtable__cell-label">{{ labelFor(cell.keys[0]) }}: </strong>
                <slot :name="`cell:${cell.keys[0]}`" :row="row">{{ row[cell.keys[0]] }}</slot>
              </template>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="(line, li) in bodyLines"
            :key="li"
            class="rtable__line rtable__line--data"
            :style="lineStyle(line)"
          >
            <div
              v-for="(cell, ci) in line"
              :key="ci"
              class="rtable__cell"
              :class="[cellClasses(cell), { 'rtable__cell--merged': cell.merged, 'rtable__cell--empty': !cell.keys.length }]"
            >
              <template v-if="cell.merged">
                <div v-for="k in cell.keys" :key="k" class="rtable__sub">
                  <strong v-if="!cell.hideLabel" class="rtable__sub-label">{{ labelFor(k) }}: </strong>
                  <slot :name="`cell:${k}`" :row="row">{{ row[k] }}</slot>
                </div>
              </template>
              <template v-else-if="cell.keys.length">
                <strong v-if="showCellLabel(cell)" class="rtable__cell-label">{{ labelFor(cell.keys[0]) }}: </strong>
                <slot
                  :name="`cell:${cell.keys[0]}`"
                  :row="row"
                  :toggle="() => toggleRow(row.id)"
                  :is-open="resolvedOpenId === row.id"
                >{{ row[cell.keys[0]] }}</slot>
              </template>
            </div>
          </div>
        </template>
      </div>
      <div
        v-if="hasExpand && resolvedOpenId === row.id"
        :key="`x-${row.id}`"
        class="rtable__expand"
      >
        <slot name="expand" :row="row" :odd="index % 2 === 0" />
      </div>
    </template>
  </div>
</template>

<script>
import {
  defineComponent, ref, computed, watch, getCurrentInstance, onMounted, onUnmounted
} from '@vue/composition-api'
import Draggable from 'vuedraggable'

// variant bundles the hideHeader/noBorders/noStripes/border/rowBg combo that
// "nested card list" tables (event popups, drill-down rows) all repeated by hand
const VARIANT_PRESETS = {
  default: {},
  nested: {
    hideHeader: true,
    noBorders: true,
    noStripes: true,
    borderColor: 'white',
    borderWidth: '2px',
    rowBg: 'var(--gray-200)'
  },
  compact: {
    noStripes: true
  }
}

// A layout "cell" is one of:
//   'key'                       -> single column
//   ['keyA', 'keyB']            -> merged cell, stacks multiple columns' values together
//   { keys: [...], span, width, class, hideLabel }  -> explicit form for edge cases
//   null                        -> empty spacer cell
function normalizeCell(spec) {
  if (spec == null) return { keys: [], merged: false }
  if (typeof spec === 'string') return { keys: [spec], merged: false }
  if (Array.isArray(spec)) return { keys: spec, merged: spec.length > 1 }
  const keys = spec.keys || []
  return {
    keys,
    merged: keys.length > 1,
    span: spec.span,
    width: spec.width,
    class: spec.class,
    hideLabel: spec.hideLabel
  }
}

export default defineComponent({
  name: 'ResponsiveTable',
  components: { Draggable },

  props: {
    // Each column: { key, label, width, minWidth, sortable, cellClass, align }
    // align: 'left' (default) | 'center' | 'right' — applies to both the header and body cells
    columns: { type: Array, required: true },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    sort: { type: String, default: null },
    direction: { type: String, default: 'asc' },
    emptyText: { type: String, default: null },
    openId: { type: [String, Number], default: undefined },
    draggable: { type: Boolean, default: false },
    // Optional (row) => class-value function, applied alongside the built-in rtable__record classes
    rowClass: { type: Function, default: null },
    // Sorted ascending: [{ maxWidth: 480, hideHeader, headerRows, accordion, rows: [[cellSpec, ...], ...] }, ...]
    // Applied when the container's width is <= maxWidth. Falls back to one row of all
    // columns (the desktop layout) when the container is wider than every breakpoint.
    // accordion: { summary: [cellSpec, ...] } — when set, `summary` renders as a single always-
    // visible clickable line (with a +/- toggle) and `rows` becomes the detail content, shown
    // only once that record is expanded (reuses the same open-state as `openId`/expand slot).
    breakpoints: { type: Array, default: () => [] },
    variant: {
      type: String,
      default: 'default',
      validator: value => Object.keys(VARIANT_PRESETS).includes(value)
    },
    hideHeader: { type: Boolean, default: null },
    noBorders: { type: Boolean, default: null },
    noStripes: { type: Boolean, default: null },
    borderColor: { type: String, default: null },
    borderWidth: { type: String, default: null },
    rowBg: { type: String, default: null }
  },

  setup(props) {
    const { proxy } = getCurrentInstance()
    const root = ref(null)
    const containerWidth = ref(Infinity)
    const internalOpenId = ref(null)

    let ro = null
    onMounted(() => {
      ro = new ResizeObserver(([entry]) => { containerWidth.value = entry.contentRect.width })
      ro.observe(root.value)
    })
    onUnmounted(() => { if (ro) ro.disconnect() })

    const cachedRows = ref([])
    watch(
      [() => props.rows, () => props.loading],
      ([rows, loading]) => { if (!loading) cachedRows.value = rows },
      { immediate: true }
    )
    const displayRows = computed(() => (props.loading ? cachedRows.value : props.rows))

    const resolvedOpenId = computed(() => (
      props.openId !== undefined ? props.openId : internalOpenId.value
    ))
    function toggleRow(id) {
      if (props.openId !== undefined) return
      internalOpenId.value = internalOpenId.value === id ? null : id
    }
    const hasExpand = computed(() => 'expand' in proxy.$scopedSlots)

    const preset = computed(() => VARIANT_PRESETS[props.variant] || {})
    const effectiveNoBorders = computed(() => (props.noBorders != null ? props.noBorders : Boolean(preset.value.noBorders)))
    const effectiveNoStripes = computed(() => (props.noStripes != null ? props.noStripes : Boolean(preset.value.noStripes)))
    const effectiveBorderColor = computed(() => (props.borderColor != null ? props.borderColor : preset.value.borderColor))
    const effectiveBorderWidth = computed(() => (props.borderWidth != null ? props.borderWidth : preset.value.borderWidth))
    const effectiveRowBg = computed(() => (props.rowBg != null ? props.rowBg : preset.value.rowBg))

    const sortedBreakpoints = computed(() => (
      [...props.breakpoints].sort((a, b) => a.maxWidth - b.maxWidth)
    ))

    // null means "no breakpoint matched" i.e. the base/desktop single-row layout applies
    const activeBreakpoint = computed(() => (
      sortedBreakpoints.value.find(bp => containerWidth.value <= bp.maxWidth) || null
    ))

    // A breakpoint's own hideHeader (if set) wins over the base prop/variant preset —
    // lets a table hide its (often redundant, once cells self-label) header only when compact
    const effectiveHideHeader = computed(() => {
      const bp = activeBreakpoint.value
      if (bp && bp.hideHeader != null) return bp.hideHeader
      return props.hideHeader != null ? props.hideHeader : Boolean(preset.value.hideHeader)
    })

    const baseRows = computed(() => [props.columns.map(c => c.key)])

    const rawLines = computed(() => (
      activeBreakpoint.value ? activeBreakpoint.value.rows : baseRows.value
    ))

    const bodyLines = computed(() => rawLines.value.map(line => line.map(normalizeCell)))

    // Header mirrors the leading N lines of the body layout, so both stay in sync from one
    // source of truth. A breakpoint can set headerRows to only header its first line(s) —
    // e.g. keep "Effective Date / Actions" as a header but not repeat it for merged/stacked
    // lines below, which already self-label their cells.
    const headerLineCount = computed(() => {
      const bp = activeBreakpoint.value
      if (bp && bp.headerRows != null) return bp.headerRows
      return bodyLines.value.length
    })
    const headerLines = computed(() => bodyLines.value.slice(0, headerLineCount.value))

    // Present only when the active breakpoint defines accordion.summary — turns bodyLines
    // into collapsed detail content, revealed per-record via the same toggleRow/resolvedOpenId
    const accordionSummary = computed(() => {
      const bp = activeBreakpoint.value
      return bp && bp.accordion ? bp.accordion.summary.map(normalizeCell) : null
    })

    // Accordion detail ignores the rows matrix's grid/line grouping — it's rendered as one
    // flat, plainly-stacked field list (like the old expandedRow pattern), not a mini-table
    const accordionDetailCells = computed(() => (
      accordionSummary.value ? bodyLines.value.reduce((acc, line) => acc.concat(line), []) : []
    ))

    const sortableColumns = computed(() => props.columns.filter(c => c.sortable))

    function findColumn(key) {
      return props.columns.find(c => c.key === key)
    }

    function labelFor(key) {
      const col = findColumn(key)
      return col ? col.label : key
    }

    function isSortable(cell) {
      if (cell.merged || !cell.keys.length) return false
      const col = findColumn(cell.keys[0])
      return Boolean(col && col.sortable)
    }

    function headerLabel(cell) {
      return cell.keys.map(labelFor).join(' / ')
    }

    function showCellLabel(cell) {
      if (cell.hideLabel) return false
      return activeBreakpoint.value != null
    }

    function colTrack(cell) {
      if (cell.width) return cell.width
      if (!activeBreakpoint.value) {
        const col = findColumn(cell.keys[0])
        const w = (col && col.width) || '1fr'
        return col && col.minWidth ? `minmax(${col.minWidth}, ${w})` : w
      }
      return `${cell.span || 1}fr`
    }

    function lineStyle(line) {
      return { gridTemplateColumns: line.map(colTrack).join(' ') }
    }

    // Same as lineStyle, plus a trailing auto-sized track for the +/- toggle icon
    function summaryLineStyle(line) {
      return { gridTemplateColumns: `${line.map(colTrack).join(' ')} auto` }
    }

    function rowClasses(row, index) {
      return [
        { 'rtable__record--open': resolvedOpenId.value === row.id, 'rtable__record--odd': index % 2 === 0, 'rtable__record--accordion': !!accordionSummary.value },
        props.rowClass ? props.rowClass(row) : null
      ]
    }

    function cellClasses(cell) {
      const fromColumns = cell.keys
        .map(k => (findColumn(k) || {}).cellClass)
        .filter(Boolean)
      const col = cell.keys.length ? findColumn(cell.keys[0]) : null
      const align = (col && col.align) || 'left'
      const alignClass = align !== 'left' ? `rtable__cell--align-${align}` : null
      return [cell.class, alignClass, ...fromColumns]
    }

    const cssVars = computed(() => {
      const vars = {}
      if (effectiveBorderColor.value != null) vars['--rt-border-color'] = effectiveBorderColor.value
      if (effectiveBorderWidth.value != null) vars['--rt-border-width'] = effectiveBorderWidth.value
      if (effectiveRowBg.value != null) vars['--rt-row-bg'] = effectiveRowBg.value
      return vars
    })

    const rootClasses = computed(() => ({
      'rtable--loading': props.loading,
      'rtable--no-borders': effectiveNoBorders.value,
      'rtable--no-stripes': effectiveNoStripes.value,
      'rtable--accordion': Boolean(accordionSummary.value)
    }))

    return {
      root,
      displayRows,
      resolvedOpenId,
      toggleRow,
      hasExpand,
      effectiveHideHeader,
      bodyLines,
      headerLines,
      accordionSummary,
      accordionDetailCells,
      sortableColumns,
      labelFor,
      isSortable,
      headerLabel,
      showCellLabel,
      lineStyle,
      summaryLineStyle,
      rowClasses,
      cellClasses,
      cssVars,
      rootClasses
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/components/ResponsiveTable.vue